import Cookies from 'js-cookie';
import type { Decision, DecisionCreateParams } from 'openai/resources/decisions';

import { getApiKeyForEnvironment } from '../ApiKeyManager';
import { awaitTurnstileToken, getTurnstileDiagnosticHeader } from '../turnstileManager';
import { Model } from '../types';

/**
 * Client for OpenAI's Decisions API, relayed through chatterway. The decision model answers typed
 * questions about a piece of input with probabilities rather than prose, so it suits small, fast
 * choices (which word should this asset land on?) that would otherwise be spent on a full LLM turn.
 */

export type DecisionChoiceQuestion = DecisionCreateParams.QuestionParamChoice;
export type DecisionChoiceAnswer = Decision.AnswerResourceChoice;

const DECISIONS_URL = '/api/chatterway/v1/decisions';
const DECISIONS_MODEL = Model.GPT_6_LUNA;

/**
 * An eval run with --direct points OPENAI_BASE_URL at OpenAI so model calls skip chatterway (whose
 * eval backend only has a stub key); decisions follow it, the same way LLMClient does.
 */
function directBaseUrl(): string | undefined {
    let baseUrl: string | undefined;
    try {
        baseUrl = process.env.OPENAI_BASE_URL;
    } catch {
        // process not defined (browser without DefinePlugin)
    }
    return baseUrl && !baseUrl.includes('chatterway') ? baseUrl : undefined;
}

async function chatterwayHeaders(): Promise<Record<string, string>> {
    const turnstileToken = await awaitTurnstileToken();
    return {
        'X-CSRFToken': Cookies.get('csrftoken') ?? '',
        'X-Turnstile-Diagnostic': getTurnstileDiagnosticHeader(),
        ...(turnstileToken ? { 'X-Turnstile-Token': turnstileToken } : {}),
    };
}

export async function requestDecisions(
    input: DecisionCreateParams['input'],
    questions: DecisionCreateParams['questions'],
): Promise<Decision> {
    const directBase = directBaseUrl();
    const response = await fetch(
        directBase ? `${directBase.replace(/\/$/, '')}/decisions` : DECISIONS_URL,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(directBase
                    ? { Authorization: `Bearer ${getApiKeyForEnvironment() ?? ''}` }
                    : await chatterwayHeaders()),
            },
            body: JSON.stringify({
                model: DECISIONS_MODEL,
                input,
                questions,
            } satisfies DecisionCreateParams),
        },
    );
    if (!response.ok) {
        throw new Error(`Decisions request failed (${response.status}): ${await response.text()}`);
    }
    return (await response.json()) as Decision;
}
